import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBackNavigation } from '@/hooks/use-back-navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    ArrowLeft,
    Calendar,
    MapPin,
    User,
    FileText,
    AlertCircle,
    CheckCircle2,
    X,
    Eye
} from 'lucide-react';
import { cn, getLocalDateString } from '@/lib/utils';
import { authenticatedFetch } from '@/lib/api';

interface Product {
    id: string;
    name: string;
    quantity: number;
    unitCost: number;
    originalCost?: number;
    mrp?: number;
    discount: number;
    sgst: number;
    cgst: number;
    igst: number;
    cess?: number;
    taxableValue: number;
    totalAmount: number;
    serialNumber?: string;
    returnQty?: number;
    productCode?: number;
    barcode?: string;
    hsnCode?: string;
    /** 1 = IGST, 0 = SGST+CGST (from api/InvoiceLineItemsForInvoiceID) */
    isIGST?: number;
    sgstPer?: number;
    cgstPer?: number;
    igstPer?: number;
    cessPer?: number;
}

interface RefundProduct extends Product {
    refundQty: number;
    refundRate: number;
    itemDiscount: number;
    invoiceDiscount: number;
    offerPrice: number;
    refundTaxable: number;
    refundAmount: number;
    refundSGST: number;
    refundCGST: number;
    refundIGST: number;
    refundCess: number;
    error?: string;
}

interface InvoiceData {
    invoiceId: string;
    invoiceNumber: string;
    customerName: string;
    date: string;
    location: string;
    products: Product[];
}

interface SettingItem {
    id: number;
    Name: string;
    Value: string;
}

interface SettingsResponse {
    Header: any[];
    EditableColumns: any[];
    ValueChangeColumns: any[];
    TwoFields: any[];
    status: number;
    message: string;
    data: SettingItem[];
    TotalRecords: number;
}

interface RefundInvoiceLineItem {
    Quantity: number;
    UnitCost: number;
    Discount: number;
    SGST: number;
    CGST: number;
    IGST: number;
    Cess: number;
    SerialNumber: string;
    ProductCode: number;
    LineTotal: number;
    OriginalCost: number;
    ECommPartner: string;
}

interface RefundInvoiceRequest {
    OriginalInvoiceID: number;
    LineItems: RefundInvoiceLineItem[];
    PaidByCash: number;
    PaidByCard: number;
    PaidByWallet: number;
    PaidByCoupon: number;
    PaidByCreditNote: number;
    PaidByCheque: number;
    Remaining: number;
    DueDate: string;
    PaidNow: boolean;
}

interface RefundInvoiceResponse {
    status: number;
    message: string;
    data: Array<{ InvoiceNumber: number; InvoiceId: number }>;
}



const ReturnProcessPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const invoiceData = location.state as InvoiceData | undefined;

    // Redirect if no data
    useEffect(() => {
        if (!invoiceData) {
            navigate('/');
        }
    }, [invoiceData, navigate]);

    const [refundProducts, setRefundProducts] = useState<RefundProduct[]>([]);

    const [creditNoteValidTill, setCreditNoteValidTill] = useState('');
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessCard, setShowSuccessCard] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [apiError, setApiError] = useState<string | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<RefundProduct | null>(null);
    
    // Payment states
    const [refundAmount, setRefundAmount] = useState(0);
    const [cashPayment, setCashPayment] = useState(0);
    const [cardPayment, setCardPayment] = useState(0);
    const [walletPayment, setWalletPayment] = useState(0);
    const [couponPayment, setCouponPayment] = useState(0);
    const [creditNotePayment, setCreditNotePayment] = useState(0);
    const [chequePayment, setChequePayment] = useState(0);
    const [duePayment, setDuePayment] = useState(0);
    const [paymentStatus, setPaymentStatus] = useState<'Paid Now' | 'Due'>('Paid Now');
    const creditNoteDateInputRef = useRef<HTMLInputElement>(null);

    // Initialize refund products and calculate credit note valid till date
    // Ref Qty field = how many to return IN THIS REQUEST (increment), not total
    useEffect(() => {
        if (invoiceData?.products) {
            const initialized = invoiceData.products.map(p => {
                const alreadyReturned = p.returnQty != null ? Math.abs(Number(p.returnQty)) : 0;
                const refundQty = 0; // Start with 0 - user enters how many MORE to return this time
                const scale = p.quantity > 0 ? refundQty / p.quantity : 0;
                const taxable = p.taxableValue;
                const sgst = p.sgst;
                const cgst = p.cgst;
                const igst = p.igst;
                const cess = p.cess ?? 0;
                const isIGST = p.isIGST === 1 ? true : p.isIGST === 0 ? false : (p.igst > 0 && p.sgst === 0 && p.cgst === 0);
                const refundSGST = isIGST ? 0 : sgst * scale;
                const refundCGST = isIGST ? 0 : cgst * scale;
                const refundIGST = isIGST ? igst * scale : 0;
                const refundCess = cess * scale;
                const refundTaxable = taxable * scale;
                const lineTotal = taxable + (isIGST ? 0 : sgst) + (isIGST ? 0 : cgst) + (isIGST ? igst : 0) + cess;
                const refundAmount = lineTotal * scale;
                const round2 = (n: number) => Math.round(n * 100) / 100;
                return {
                    ...p,
                    refundQty,
                    refundRate: p.unitCost,
                    itemDiscount: p.discount,
                    invoiceDiscount: 0,
                    refundTaxable,
                    refundSGST,
                    refundCGST,
                    refundIGST,
                    refundCess,
                    refundAmount,
                    offerPrice: round2(refundAmount)
                };
            });
            setRefundProducts(initialized);
        }

        // Fetch settings to calculate Credit Note Valid Till date
        const fetchSettingsAndCalculateDate = async () => {
            try {
                const settingsResponse = await authenticatedFetch<SettingsResponse>('/api/Settings?Count=100');
                
                if (settingsResponse.data && settingsResponse.data.data) {
                    const refundsSetting = settingsResponse.data.data.find(
                        setting => setting.Name === "Refunds CN No of Days"
                    );
                    
                    if (refundsSetting && refundsSetting.Value) {
                        // Parse the setting value (e.g., "1:D" for 1 Day)
                        const [number, unit] = refundsSetting.Value.split(':');
                        const daysToAdd = parseInt(number) || 1;
                        
                        // Calculate the valid till date
                        const today = new Date();
                        let validTillDate = new Date(today);
                        
                        switch (unit) {
                            case 'D': // Days
                                validTillDate.setDate(today.getDate() + daysToAdd);
                                break;
                            case 'W': // Weeks
                                validTillDate.setDate(today.getDate() + (daysToAdd * 7));
                                break;
                            case 'M': // Months
                                validTillDate.setMonth(today.getMonth() + daysToAdd);
                                break;
                            default:
                                validTillDate.setDate(today.getDate() + daysToAdd);
                        }
                        
                        // Format as yyyy-mm-dd for input (local timezone)
                        const formattedDate = getLocalDateString(validTillDate);
                        setCreditNoteValidTill(formattedDate);
                    } else {
                        // Default to 1 day if setting not found
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        setCreditNoteValidTill(getLocalDateString(tomorrow));
                    }
                }
            } catch (error) {
                console.error('Failed to fetch settings:', error);
                // Default to 1 day if API call fails
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                setCreditNoteValidTill(getLocalDateString(tomorrow));
            }
        };

        fetchSettingsAndCalculateDate();
    }, [invoiceData]);

    // Calculate totals
    // Calculate totals
    const totals = refundProducts.reduce(
        (acc, p) => ({
            refundQty: acc.refundQty + p.refundQty,
            refundRate: acc.refundRate + p.refundRate,
            itemDiscount: acc.itemDiscount + p.itemDiscount,
            invoiceDiscount: acc.invoiceDiscount + p.invoiceDiscount,
            offerPrice: acc.offerPrice + p.offerPrice,
            refundTaxable: acc.refundTaxable + p.refundTaxable,
            refundSGST: acc.refundSGST + p.refundSGST,
            refundCGST: acc.refundCGST + p.refundCGST,
            refundIGST: acc.refundIGST + p.refundIGST,
            refundCess: acc.refundCess + p.refundCess,
            refundAmount: acc.refundAmount + p.refundAmount
        }),
        {
            refundQty: 0, refundRate: 0, itemDiscount: 0, invoiceDiscount: 0,
            offerPrice: 0, refundTaxable: 0, refundSGST: 0, refundCGST: 0,
            refundIGST: 0, refundCess: 0, refundAmount: 0
        }
    );

    // Initialize payment amounts when totals change
    useEffect(() => {
        setRefundAmount(totals.refundAmount);
        const round2 = (n: number) => Math.round(n * 100) / 100;
        if (paymentStatus === 'Paid Now') {
            setCashPayment(round2(totals.refundAmount));
            setCardPayment(0);
            setWalletPayment(0);
            setCouponPayment(0);
            setCreditNotePayment(0);
            setChequePayment(0);
            setDuePayment(0);
        } else {
            setCashPayment(0);
            setCardPayment(0);
            setWalletPayment(0);
            setCouponPayment(0);
            setCreditNotePayment(0);
            setChequePayment(0);
            setDuePayment(round2(totals.refundAmount));
        }
    }, [totals.refundAmount, paymentStatus]);

    // Validate payment amounts
    const validatePayments = (): boolean => {
        setApiError(null);
        if (paymentStatus === 'Paid Now') {
            const totalPayments = cashPayment + cardPayment + walletPayment + couponPayment + creditNotePayment + chequePayment + duePayment;
            const difference = Math.abs(totalPayments - refundAmount);
            if (difference > 0.01) {
                setApiError(`Payment amounts must equal the refund amount (₹${refundAmount.toFixed(2)}). Current total: ₹${totalPayments.toFixed(2)}`);
                return false;
            }
            if (duePayment > 0) {
                setApiError('Due amount must be 0 when payment status is "Paid Now"');
                return false;
            }
        }
        return true;
    };

    // Calculate payment due


    const handleFieldChange = (productId: string, field: keyof RefundProduct, value: string) => {
        setRefundProducts(prev => prev.map(p => {
            if (p.id !== productId) return p;

            const numValue = parseFloat(value) || 0;
            const updated = { ...p, [field]: numValue };

            // Find original product to get tax ratios (respect isIGST: SGST+CGST vs IGST)
            const original = invoiceData?.products.find(op => op.id === productId);
            if (original) {
                const isIGST = original.isIGST === 1 ? true : original.isIGST === 0 ? false : (original.igst > 0 && original.sgst === 0 && original.cgst === 0);
                const taxRatioSGST = !isIGST && original.taxableValue > 0 ? original.sgst / original.taxableValue : 0;
                const taxRatioCGST = !isIGST && original.taxableValue > 0 ? original.cgst / original.taxableValue : 0;
                const taxRatioIGST = isIGST && original.taxableValue > 0 ? original.igst / original.taxableValue : 0;
                const taxRatioCess = original.taxableValue > 0 ? (original.cess ?? 0) / original.taxableValue : 0;

                // Recalculate Logic
                // 1. Base Taxable = Qty * Rate
                // 2. Net Taxable = Base Taxable - Item Disc - Inv Disc
                // 3. Taxes = Net Taxable * Ratios
                // 4. Total = Net Taxable + Taxes

                const qty = field === 'refundQty' ? numValue : p.refundQty;
                const rate = field === 'refundRate' ? numValue : p.refundRate;
                const itemDisc = field === 'itemDiscount' ? numValue : p.itemDiscount;
                const invDisc = field === 'invoiceDiscount' ? numValue : p.invoiceDiscount;

                const baseTaxable = qty * rate;
                // Ensure taxable doesn't go below zero
                const netTaxable = Math.max(0, baseTaxable - itemDisc - invDisc);

                updated.refundTaxable = netTaxable;
                updated.refundSGST = netTaxable * taxRatioSGST;
                updated.refundCGST = netTaxable * taxRatioCGST;
                updated.refundIGST = netTaxable * taxRatioIGST;
                updated.refundCess = netTaxable * taxRatioCess;
                updated.refundAmount = netTaxable + updated.refundSGST + updated.refundCGST + updated.refundIGST + updated.refundCess;

                // Also update offerPrice to reflect the new total (rounded to 2 decimal places)
                updated.offerPrice = Math.round(updated.refundAmount * 100) / 100;
            }

            // Ref Qty validation: cannot exceed remaining returnable (original qty - already returned)
            // Field = increment (how many to return in this request). ReturnQty from API is negative.
            if (field === 'refundQty') {
                const alreadyReturned = p.returnQty != null ? Math.abs(Number(p.returnQty)) : 0;
                const maxReturnable = p.quantity - alreadyReturned;
                if (numValue > maxReturnable) {
                    updated.error = alreadyReturned > 0
                        ? `Already returned ${alreadyReturned}. You can return only ${maxReturnable} more (max ${p.quantity} total).`
                        : `Return quantity cannot exceed original invoice quantity (${p.quantity}).`;
                } else {
                    updated.error = undefined;
                }
            } else {
                updated.error = undefined;
            }

            return updated;
        }));
    };

    const handleProcessRefund = () => {
        setApiError(null);
        const hasErrors = refundProducts.some(p => p.error);
        if (hasErrors) {
            setApiError('Please fix all errors before processing');
            return;
        }

        // Validate payment amounts
        if (!validatePayments()) {
            return;
        }

        // Directly process refund without confirmation dialog
        confirmRefund();
    };

    // Navigate back: close confirm dialog first, else to Invoice Details if we came from it, else to Refunds list
    const handleBackFromCreditNote = () => {
        if (showConfirmDialog) {
            setShowConfirmDialog(false);
            return;
        }
        const state = location.state as (InvoiceData & { invoice?: unknown }) | undefined;
        if (state?.invoice) {
            navigate('/refunds/invoice', { state: { invoice: state.invoice } });
        } else {
            window.dispatchEvent(new CustomEvent('showRefunds', { detail: { refresh: true } }));
            navigate('/', { state: { showRefunds: true, refresh: true } });
        }
    };

    // Android / mobile web back: Credit Note -> Invoice Details -> Refunds (only on mobile - desktop unchanged)
    useBackNavigation({
        onBack: handleBackFromCreditNote,
        canGoBack: true,
        showExitConfirmation: false,
    });

    const confirmRefund = async () => {
        if (!invoiceData) return;

        // Don't include products with qty 0 in the POST body
        const candidates = refundProducts.filter(p => p.refundQty > 0);

        // Before POST: validate - refundQty (increment) must be <= (original qty - already returned)
        const exceeds: RefundProduct[] = [];
        const validForPost: RefundProduct[] = [];
        for (const p of candidates) {
            const alreadyReturned = p.returnQty != null ? Math.abs(Number(p.returnQty)) : 0;
            const maxReturnable = p.quantity - alreadyReturned;
            if (p.refundQty > maxReturnable) {
                exceeds.push(p);
            } else {
                validForPost.push(p);
            }
        }
        if (exceeds.length > 0) {
            const names = exceeds.map(p => p.name).join(', ');
            setApiError(`Return quantity exceeds remaining returnable for: ${names}. Please correct the values.`);
            return;
        }

        const lineItemsWithQty = validForPost;
        const totalRefundAmount = lineItemsWithQty.reduce((sum, p) => sum + p.refundAmount, 0);

        const isPaidNow = paymentStatus === 'Paid Now';
        const payload: RefundInvoiceRequest = {
            OriginalInvoiceID: Number(invoiceData.invoiceId),
            LineItems: lineItemsWithQty.map(p => {
                // Send GST as percentage values, not amounts. Use isIGST to determine SGST/CGST vs IGST.
                let isIGST = p.isIGST === 1;
                let sgstPer = p.sgstPer ?? 0;
                let cgstPer = p.cgstPer ?? 0;
                let igstPer = p.igstPer ?? 0;
                const cessPer = p.cessPer ?? 0;

                // Fallback: when percentages not provided, infer isIGST from amounts and derive percentages
                if (sgstPer === 0 && cgstPer === 0 && igstPer === 0 && p.refundTaxable > 0) {
                    if (p.isIGST === undefined || p.isIGST === null) {
                        isIGST = p.igst > 0 && p.sgst === 0 && p.cgst === 0;
                    }
                    if (isIGST) {
                        igstPer = (p.refundIGST / p.refundTaxable) * 100;
                        sgstPer = 0;
                        cgstPer = 0;
                    } else {
                        sgstPer = (p.refundSGST / p.refundTaxable) * 100;
                        cgstPer = (p.refundCGST / p.refundTaxable) * 100;
                        igstPer = 0;
                    }
                }

                const SGST = isIGST ? 0 : Math.round(sgstPer * 100) / 100;
                const CGST = isIGST ? 0 : Math.round(cgstPer * 100) / 100;
                const IGST = isIGST ? Math.round(igstPer * 100) / 100 : 0;
                const Cess = Math.round(cessPer * 100) / 100;

                return {
                    Quantity: Math.abs(p.refundQty),
                    UnitCost: p.refundRate,
                    Discount: p.itemDiscount,
                    SGST,
                    CGST,
                    IGST,
                    Cess,
                    SerialNumber: p.serialNumber || "",
                    ProductCode: p.productCode || Number(p.id),
                    LineTotal: Math.round(p.refundAmount * 100) / 100,
                    OriginalCost: p.originalCost ?? 0,
                    ECommPartner: "Sales"
                };
            }),
            PaidByCash: Math.round((isPaidNow ? cashPayment : 0) * 100) / 100,
            PaidByCard: Math.round((isPaidNow ? cardPayment : 0) * 100) / 100,
            PaidByWallet: Math.round((isPaidNow ? walletPayment : 0) * 100) / 100,
            PaidByCoupon: Math.round((isPaidNow ? couponPayment : 0) * 100) / 100,
            PaidByCreditNote: Math.round((isPaidNow ? creditNotePayment : 0) * 100) / 100,
            PaidByCheque: Math.round((isPaidNow ? chequePayment : 0) * 100) / 100,
            Remaining: Math.round((isPaidNow ? duePayment : refundAmount) * 100) / 100,
            DueDate: creditNoteValidTill || getLocalDateString(),
            PaidNow: isPaidNow
        };

        // === POST API JSON LOGGING ===
        console.log('=== POST api/RefundInvoice ===');
        console.log('Request JSON:', JSON.stringify(payload, null, 2));
        console.log('================================');

        try {
            const response = await authenticatedFetch<RefundInvoiceResponse>('/api/RefundInvoice', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            console.log('=== POST api/RefundInvoice Response ===');
            console.log('Response JSON:', JSON.stringify(response?.data ?? response, null, 2));
            console.log('=======================================');

            const body = response.data;
            if (body.status === 0 && body.data && body.data.length > 0) {
                setApiError(null);
                setShowConfirmDialog(false);
                setSuccessMessage(`Invoice Number ${body.data[0].InvoiceNumber} created successfully.`);
                setShowSuccessCard(true);
                // Always navigate to Refunds / Returns list after success (not back to invoice details)
                setTimeout(() => {
                    window.dispatchEvent(new CustomEvent('showRefunds', { detail: { refresh: true } }));
                    navigate('/', { state: { showRefunds: true, refresh: true } });
                }, 3000);
            } else {
                setApiError(body?.message || 'Credit note could not be generated.');
            }
        } catch (error) {
            console.error('❌ RefundInvoice API error:', error);
            setApiError(error instanceof Error ? error.message : 'Error generating credit note. Please try again.');
        }
    };

    const hasValidationErrors = refundProducts.some(p => p.error);
    // Enable Generate Credit Note when any product has ref qty >= 1
    const canGenerateCreditNote = refundProducts.some(p => p.refundQty >= 1);

    if (!invoiceData) {
        return null;
    }

    return (
        <div className="min-h-screen bg-[#f8fafc] pb-36 md:pb-28 font-sans text-slate-800">
            <style>{`
                input[type=number]::-webkit-inner-spin-button, 
                input[type=number]::-webkit-outer-spin-button { 
                    -webkit-appearance: none; 
                    margin: 0; 
                }
                input[type=number] {
                    -moz-appearance: textfield;
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }
            `}</style>

            {/* Header Section */}
            <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1920px] mx-auto">
                    {/* Top Bar */}
                    <div className="flex items-center justify-between px-4 md:px-6 py-2 md:py-3 border-b border-slate-100">
                        <div className="flex items-center gap-2 md:gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleBackFromCreditNote}
                                className="hover:bg-slate-100 rounded-full text-slate-600 h-8 w-8 md:h-10 md:w-10"
                            >
                                <ArrowLeft className="h-4 w-4 md:h-5 md:w-5" />
                            </Button>
                            <h1 className="text-base md:text-xl font-bold text-slate-900">Invoice Return Processing</h1>
                        </div>
                        <div className="flex items-center gap-2 md:gap-3">
                            <Badge variant="outline" className="px-2 py-0.5 md:px-3 md:py-1 bg-slate-50 text-slate-600 border-slate-200 font-medium text-xs md:text-sm">
                                Invoice #{invoiceData.invoiceNumber}
                            </Badge>
                        </div>
                    </div>

                    {/* Info Bar - Mobile: 2-row × 4-column grid with smaller fonts; Desktop: unchanged */}
                    <div className="px-4 md:px-8 py-2 md:py-6 bg-slate-50/30 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-8">
                        {/* Invoice No */}
                        <div className="flex items-center gap-1.5 md:gap-4 group">
                            <div className="p-1.5 md:p-3 bg-white rounded-lg md:rounded-xl border border-slate-100 shadow-sm group-hover:border-blue-100 group-hover:shadow-md transition-all shrink-0">
                                <FileText className="h-3 w-3 md:h-6 md:w-6 text-slate-400 group-hover:text-blue-600 transition-colors" />
                            </div>
                            <div className="flex flex-col min-w-0 overflow-hidden">
                                <span className="text-[9px] md:text-[11px] uppercase font-bold text-slate-400 tracking-wider md:tracking-widest mb-0.5">Invoice No</span>
                                <span className="text-xs md:text-xl font-bold text-slate-900 leading-tight truncate" title={invoiceData.invoiceNumber}>{invoiceData.invoiceNumber}</span>
                            </div>
                        </div>

                        {/* Customer */}
                        <div className="flex items-center gap-1.5 md:gap-4 group">
                            <div className="p-1.5 md:p-3 bg-white rounded-lg md:rounded-xl border border-slate-100 shadow-sm group-hover:border-purple-100 group-hover:shadow-md transition-all shrink-0">
                                <User className="h-3 w-3 md:h-6 md:w-6 text-slate-400 group-hover:text-purple-600 transition-colors" />
                            </div>
                            <div className="flex flex-col min-w-0 overflow-hidden">
                                <span className="text-[9px] md:text-[11px] uppercase font-bold text-slate-400 tracking-wider md:tracking-widest mb-0.5">Customer</span>
                                <span className="text-xs md:text-xl font-bold text-slate-900 leading-tight truncate" title={invoiceData.customerName}>{invoiceData.customerName}</span>
                            </div>
                        </div>

                        {/* Date */}
                        <div className="flex items-center gap-1.5 md:gap-4 group">
                            <div className="p-1.5 md:p-3 bg-white rounded-lg md:rounded-xl border border-slate-100 shadow-sm group-hover:border-emerald-100 group-hover:shadow-md transition-all shrink-0">
                                <Calendar className="h-3 w-3 md:h-6 md:w-6 text-slate-400 group-hover:text-emerald-600 transition-colors" />
                            </div>
                            <div className="flex flex-col min-w-0 overflow-hidden">
                                <span className="text-[9px] md:text-[11px] uppercase font-bold text-slate-400 tracking-wider md:tracking-widest mb-0.5">Date</span>
                                <span className="text-xs md:text-xl font-bold text-slate-900 leading-tight truncate">{invoiceData.date}</span>
                            </div>
                        </div>

                        {/* Location */}
                        <div className="flex items-center gap-1.5 md:gap-4 group">
                            <div className="p-1.5 md:p-3 bg-white rounded-lg md:rounded-xl border border-slate-100 shadow-sm group-hover:border-orange-100 group-hover:shadow-md transition-all shrink-0">
                                <MapPin className="h-3 w-3 md:h-6 md:w-6 text-slate-400 group-hover:text-orange-600 transition-colors" />
                            </div>
                            <div className="flex flex-col min-w-0 overflow-hidden">
                                <span className="text-[9px] md:text-[11px] uppercase font-bold text-slate-400 tracking-wider md:tracking-widest mb-0.5">Location</span>
                                <span className="text-xs md:text-xl font-bold text-slate-900 leading-tight truncate" title={invoiceData.location}>{invoiceData.location}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-[1920px] mx-auto px-4 md:px-6 py-4 md:py-8">
                <Card className="border border-slate-200 shadow-sm bg-white rounded-lg overflow-hidden">
                    <CardHeader className="bg-white border-b border-slate-100 py-2 md:py-4 px-4 md:px-6">
                        <CardTitle className="text-sm md:text-base font-bold text-slate-800">Return Items</CardTitle>
                    </CardHeader>

                    {/* Mobile: Card-based UI */}
                    <div className="md:hidden overflow-y-auto max-h-[calc(100vh-380px)]">
                        <div className="p-4 space-y-4">
                            {refundProducts.map((product) => (
                                <div
                                    key={product.id}
                                    className="bg-slate-50 rounded-xl border border-slate-200 p-4 space-y-3"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-slate-800 text-sm truncate">{product.name}</p>
                                            {product.error && (
                                                <div className="flex items-center text-xs text-rose-600 mt-1 font-medium">
                                                    <AlertCircle className="h-3 w-3 mr-1 shrink-0" />
                                                    {product.error}
                                                </div>
                                            )}
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 rounded-full text-blue-600 hover:bg-blue-50 shrink-0"
                                            onClick={() => setSelectedProduct(product)}
                                        >
                                            <Eye className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                        <div>
                                            <span className="text-slate-500">Serial No</span>
                                            <p className="font-medium text-slate-800">{product.serialNumber || '-'}</p>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Received Qty</span>
                                            <p className="font-medium text-slate-800">
                                                {product.quantity}
                                                {(product.returnQty != null && Math.abs(Number(product.returnQty)) > 0) && (
                                                    <span className="text-xs text-amber-600 ml-1">(already ret'd: {Math.abs(Number(product.returnQty))})</span>
                                                )}
                                            </p>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Refund Qty</span>
                                            <div className="mt-0.5">
                                                <Input
                                                    type="number"
                                                    value={product.refundQty}
                                                    onChange={(e) => handleFieldChange(product.id, 'refundQty', e.target.value)}
                                                    className={cn(
                                                        "w-full h-8 text-center font-bold text-blue-700 bg-white border border-blue-200 rounded text-xs",
                                                        product.error && "bg-rose-50 border-rose-300 text-rose-600"
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <span className="text-slate-500">Unit Cost</span>
                                            <div className="mt-0.5">
                                                <Input
                                                    type="number"
                                                    value={product.refundRate}
                                                    onChange={(e) => handleFieldChange(product.id, 'refundRate', e.target.value)}
                                                    className="w-full h-8 text-right bg-white border-slate-200 text-slate-700 font-medium text-xs"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-slate-200 space-y-1 text-xs">
                                        <div className="flex justify-between text-slate-500">
                                            <span>SGST</span>
                                            <span>₹{product.refundSGST.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-slate-500">
                                            <span>CGST</span>
                                            <span>₹{product.refundCGST.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-slate-500">
                                            <span>IGST</span>
                                            <span>₹{product.refundIGST.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-slate-500">
                                            <span>CESS</span>
                                            <span>₹{product.refundCess.toFixed(2)}</span>
                                        </div>
                                    </div>
                                    <div className="pt-2 border-t border-slate-200 flex justify-between items-center">
                                        <span className="text-xs font-bold text-slate-600">Refund Total</span>
                                        <span className="font-bold text-slate-900">₹{product.refundAmount.toFixed(2)}</span>
                                    </div>
                                </div>
                            ))}
                            {/* Mobile totals card */}
                            <div className="bg-slate-100 rounded-xl border border-slate-200 p-4">
                                <div className="flex justify-between items-center">
                                    <span className="font-bold text-slate-700 text-sm">Total</span>
                                    <span className="text-lg font-bold text-blue-700">₹{totals.refundAmount.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Desktop: Table layout */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-xs md:text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-2 md:px-6 py-2 md:py-3 text-left text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">PRODUCT</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-left text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">SERIAL NO</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-center text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">REC QTY</th>
                                    <th className="px-1 md:px-2 py-2 md:py-3 text-center text-[9px] md:text-[11px] font-bold text-blue-700 uppercase tracking-wider bg-blue-50/50">REF QTY</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-blue-700 uppercase tracking-wider">Unit Cost</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-blue-700 uppercase tracking-wider">ITEM DISC</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-blue-700 uppercase tracking-wider">INV DISC</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-blue-700 uppercase tracking-wider">OFFER PRICE</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-blue-700 uppercase tracking-wider">TAXABLE</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">SGST</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">CGST</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">IGST</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">Cess</th>
                                    <th className="px-2 md:px-6 py-2 md:py-3 text-right text-[9px] md:text-[11px] font-bold text-slate-800 uppercase tracking-wider bg-slate-100/50">REF TOTAL</th>
                                    <th className="px-2 md:px-4 py-2 md:py-3 text-center text-[9px] md:text-[11px] font-bold text-slate-500 uppercase tracking-wider">ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {refundProducts.map((product) => (
                                    <tr
                                        key={product.id}
                                        className={cn(
                                            "transition-colors duration-150 ease-in-out group",
                                            "hover:bg-slate-50"
                                        )}
                                    >
                                        <td className="px-2 md:px-6 py-1 md:py-3 font-medium text-slate-800 max-w-[120px] md:max-w-[220px] truncate text-xs md:text-sm">
                                            {product.name}
                                            {product.error && (
                                                <div className="flex items-center text-xs text-rose-600 mt-1 font-medium animate-pulse">
                                                    <AlertCircle className="h-3 w-3 mr-1.5" />
                                                    {product.error}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-left">
                                            {product.serialNumber ? (
                                                <span className="font-mono text-xs text-slate-700 font-semibold">
                                                    {product.serialNumber}
                                                </span>
                                            ) : (
                                                <span className="text-slate-300">-</span>
                                            )}
                                        </td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-center">
                                            <span className="bg-slate-100 text-slate-600 px-1 md:px-2 py-0.5 md:py-1 rounded text-xs font-semibold">
                                                {product.quantity}
                                                {(product.returnQty != null && Math.abs(Number(product.returnQty)) > 0) && (
                                                    <span className="block text-[10px] text-amber-600 mt-0.5">ret'd: {Math.abs(Number(product.returnQty))}</span>
                                                )}
                                            </span>
                                        </td>

                                        {/* Editable Fields */}
                                        <td className="px-1 md:px-2 py-1 md:py-3 bg-blue-50/10 group-hover:bg-blue-50/30 transition-colors border-x border-dashed border-blue-100">
                                            <div className="flex justify-center">
                                                <Input
                                                    type="number"
                                                    value={product.refundQty}
                                                    onChange={(e) => handleFieldChange(product.id, 'refundQty', e.target.value)}
                                                    className={cn(
                                                        "w-12 md:w-16 h-6 md:h-8 text-center font-bold text-blue-700 bg-white border border-blue-200 rounded shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-xs md:text-sm",
                                                        product.error && "bg-rose-50 border-rose-300 text-rose-600 focus:ring-rose-500"
                                                    )}
                                                />
                                            </div>
                                        </td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right">
                                            <Input
                                                type="number"
                                                value={product.refundRate}
                                                onChange={(e) => handleFieldChange(product.id, 'refundRate', e.target.value)}
                                                className="w-16 md:w-20 h-6 md:h-8 text-right bg-transparent border-transparent hover:border-slate-200 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded text-slate-700 font-medium ml-auto text-xs md:text-sm"
                                            />
                                        </td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right">
                                            <Input
                                                type="number"
                                                value={product.itemDiscount}
                                                onChange={(e) => handleFieldChange(product.id, 'itemDiscount', e.target.value)}
                                                className="w-12 md:w-16 h-6 md:h-8 text-right bg-transparent border-transparent hover:border-slate-200 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded text-slate-700 font-medium ml-auto text-xs md:text-sm"
                                            />
                                        </td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right">
                                            <Input
                                                type="number"
                                                value={product.invoiceDiscount}
                                                onChange={(e) => handleFieldChange(product.id, 'invoiceDiscount', e.target.value)}
                                                className="w-12 md:w-16 h-6 md:h-8 text-right bg-transparent border-transparent hover:border-slate-200 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded text-slate-700 font-medium ml-auto text-xs md:text-sm"
                                            />
                                        </td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right">
                                            <Input
                                                type="number"
                                                value={product.offerPrice}
                                                onChange={(e) => handleFieldChange(product.id, 'offerPrice', e.target.value)}
                                                className="w-16 md:w-20 h-6 md:h-8 text-right bg-transparent border-transparent hover:border-slate-200 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded text-slate-700 font-medium ml-auto text-xs md:text-sm"
                                            />
                                        </td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right">
                                            <Input
                                                type="number"
                                                value={product.refundTaxable}
                                                onChange={(e) => handleFieldChange(product.id, 'refundTaxable', e.target.value)}
                                                className="w-16 md:w-24 h-6 md:h-8 text-right font-bold text-blue-700 bg-transparent border-transparent hover:border-slate-200 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded ml-auto text-xs md:text-sm"
                                            />
                                        </td>

                                        {/* Read Only Stats */}
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right text-slate-500 text-xs">₹{product.refundSGST.toFixed(2)}</td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right text-slate-500 text-xs">₹{product.refundCGST.toFixed(2)}</td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right text-slate-500 text-xs">₹{product.refundIGST.toFixed(2)}</td>
                                        <td className="px-2 md:px-4 py-1 md:py-3 text-right text-slate-500 text-xs">₹{product.refundCess.toFixed(2)}</td>

                                        {/* Total */}
                                        <td className="px-2 md:px-6 py-1 md:py-3 text-right bg-slate-50/30">
                                            <span className="font-bold text-slate-800 text-xs md:text-sm">₹{product.refundAmount.toFixed(2)}</span>
                                        </td>

                                        <td className="px-2 md:px-4 py-1 md:py-3 text-center">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-6 w-6 md:h-7 md:w-7 p-0 rounded-full text-blue-600 hover:bg-blue-50"
                                                onClick={() => setSelectedProduct(product)}
                                            >
                                                <Eye className="h-3 w-3 md:h-4 md:w-4" />
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="bg-slate-50 font-semibold text-slate-700 border-t border-slate-200">
                                <tr>
                                    <td className="px-6 py-4 text-left" colSpan={2}>Total</td>
                                    <td className="px-4 py-4 text-center text-slate-400">-</td>
                                    <td className="px-2 py-4 text-center font-bold text-blue-700 bg-blue-50/30 border-x border-blue-100">{totals.refundQty}</td>
                                    <td className="px-4 py-4 text-right">₹{totals.refundRate.toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right">₹{totals.itemDiscount.toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right">₹{totals.invoiceDiscount.toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right">₹{(Math.round(totals.offerPrice * 100) / 100).toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right text-blue-700">₹{totals.refundTaxable.toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right text-slate-500">₹{totals.refundSGST.toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right text-slate-500">₹{totals.refundCGST.toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right text-slate-500">₹{totals.refundIGST.toFixed(2)}</td>
                                    <td className="px-4 py-4 text-right text-slate-500">₹{totals.refundCess.toFixed(2)}</td>
                                    <td className="px-6 py-4 text-right bg-slate-100/50">
                                        <span className="text-lg font-bold text-blue-700">₹{totals.refundAmount.toFixed(2)}</span>
                                    </td>
                                    <td className="px-4 py-4"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </Card>
            </div>

            {/* Sticky Bottom Actions Bar - on mobile: compact + scrollable to ensure totals/button visible */}
            <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-slate-200 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] md:p-3">
                <div className="max-w-[1920px] mx-auto flex flex-col md:gap-3 px-3 md:px-6 max-h-[50vh] md:max-h-none overflow-y-auto md:overflow-visible overscroll-contain p-2 md:p-0 gap-1.5 md:gap-3">
                    {/* API/Validation Error Card */}
                    {apiError && (
                        <div className="bg-red-600 text-white px-4 py-3 rounded-lg shadow-md flex items-center justify-between gap-3">
                            <span className="text-sm font-medium">{apiError}</span>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setApiError(null)}
                                className="text-white hover:bg-red-700/50 h-8 px-2 shrink-0"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    )}
                    {/* Payment Section - compact layout to maximize products table visibility */}
                    <div className="bg-slate-50 rounded-lg border border-slate-200 p-1.5 md:p-3">
                        {/* Mobile: horizontally scrollable payment fields - compact for small screens */}
                        <div className="md:hidden overflow-x-auto pb-1 -mx-0.5 scroll-smooth">
                            <div className="flex gap-2 min-w-max px-0.5">
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Refund</Label>
                                    <Input
                                        type="number"
                                        value={totals.refundAmount.toFixed(2)}
                                        disabled
                                        className="h-6 text-[10px] bg-slate-100 border-slate-300 text-slate-700 font-semibold"
                                    />
                                </div>
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Cash</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={paymentStatus === 'Paid Now' ? (Math.round(cashPayment * 100) / 100) : 0}
                                        onChange={(e) => setCashPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                        disabled={paymentStatus !== 'Paid Now'}
                                        className={cn("h-6 text-[10px] font-medium", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                    />
                                </div>
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Card</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={paymentStatus === 'Paid Now' ? (Math.round(cardPayment * 100) / 100) : 0}
                                        onChange={(e) => setCardPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                        disabled={paymentStatus !== 'Paid Now'}
                                        className={cn("h-6 text-[10px] font-medium", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                    />
                                </div>
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Wallet</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={paymentStatus === 'Paid Now' ? (Math.round(walletPayment * 100) / 100) : 0}
                                        onChange={(e) => setWalletPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                        disabled={paymentStatus !== 'Paid Now'}
                                        className={cn("h-6 text-[10px] font-medium", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                    />
                                </div>
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Coupon</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={paymentStatus === 'Paid Now' ? (Math.round(couponPayment * 100) / 100) : 0}
                                        onChange={(e) => setCouponPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                        disabled={paymentStatus !== 'Paid Now'}
                                        className={cn("h-6 text-[10px] font-medium", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                    />
                                </div>
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Cr. Note</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={paymentStatus === 'Paid Now' ? (Math.round(creditNotePayment * 100) / 100) : 0}
                                        onChange={(e) => setCreditNotePayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                        disabled={paymentStatus !== 'Paid Now'}
                                        className={cn("h-6 text-[10px] font-medium", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                    />
                                </div>
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Cheque</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={paymentStatus === 'Paid Now' ? (Math.round(chequePayment * 100) / 100) : 0}
                                        onChange={(e) => setChequePayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                        disabled={paymentStatus !== 'Paid Now'}
                                        className={cn("h-6 text-[10px] font-medium", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                    />
                                </div>
                                <div className="w-16 shrink-0">
                                    <Label className="text-[9px] font-medium text-slate-600 mb-0.5 block">Due</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={paymentStatus === 'Paid Now' ? (Math.round(duePayment * 100) / 100) : (Math.round(refundAmount * 100) / 100)}
                                        onChange={(e) => setDuePayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                        disabled={paymentStatus !== 'Paid Now'}
                                        className={cn("h-6 text-[10px] font-medium", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Desktop: compact grid layout */}
                        <div className="hidden md:grid grid-cols-4 lg:grid-cols-8 gap-2 gap-y-1.5">
                            {/* Refund Amount */}
                            <div className="col-span-1">
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Refund Amount</Label>
                                <Input
                                    type="number"
                                    value={totals.refundAmount.toFixed(2)}
                                    disabled
                                    className="w-full h-7 bg-slate-100 border-slate-300 text-slate-700 font-semibold text-xs"
                                />
                            </div>
                            
                            {/* Cash */}
                            <div>
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Cash</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentStatus === 'Paid Now' ? (Math.round(cashPayment * 100) / 100) : 0}
                                    onChange={(e) => setCashPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                    disabled={paymentStatus !== 'Paid Now'}
                                    className={cn("w-full h-7 font-medium text-xs", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                />
                            </div>
                            
                            {/* Card */}
                            <div>
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Card</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentStatus === 'Paid Now' ? (Math.round(cardPayment * 100) / 100) : 0}
                                    onChange={(e) => setCardPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                    disabled={paymentStatus !== 'Paid Now'}
                                    className={cn("w-full h-7 font-medium text-xs", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                />
                            </div>
                            
                            {/* Wallet */}
                            <div>
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Wallet</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentStatus === 'Paid Now' ? (Math.round(walletPayment * 100) / 100) : 0}
                                    onChange={(e) => setWalletPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                    disabled={paymentStatus !== 'Paid Now'}
                                    className={cn("w-full h-7 font-medium text-xs", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                />
                            </div>
                            
                            {/* Coupon */}
                            <div>
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Coupon</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentStatus === 'Paid Now' ? (Math.round(couponPayment * 100) / 100) : 0}
                                    onChange={(e) => setCouponPayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                    disabled={paymentStatus !== 'Paid Now'}
                                    className={cn("w-full h-7 font-medium text-xs", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                />
                            </div>
                            
                            {/* Credit Note */}
                            <div>
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Cr. Note</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentStatus === 'Paid Now' ? (Math.round(creditNotePayment * 100) / 100) : 0}
                                    onChange={(e) => setCreditNotePayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                    disabled={paymentStatus !== 'Paid Now'}
                                    className={cn("w-full h-7 font-medium text-xs", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                />
                            </div>
                            
                            {/* Cheque */}
                            <div>
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Cheque</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentStatus === 'Paid Now' ? (Math.round(chequePayment * 100) / 100) : 0}
                                    onChange={(e) => setChequePayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                    disabled={paymentStatus !== 'Paid Now'}
                                    className={cn("w-full h-7 font-medium text-xs", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                />
                            </div>
                            
                            {/* Due */}
                            <div>
                                <Label className="text-[11px] font-medium text-slate-600 mb-0.5 block">Due</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={paymentStatus === 'Paid Now' ? (Math.round(duePayment * 100) / 100) : (Math.round(refundAmount * 100) / 100)}
                                    onChange={(e) => setDuePayment(Math.round((parseFloat(e.target.value) || 0) * 100) / 100)}
                                    disabled={paymentStatus !== 'Paid Now'}
                                    className={cn("w-full h-7 font-medium text-xs", paymentStatus === 'Paid Now' ? "border-slate-300 text-slate-700" : "bg-slate-100 border-slate-200 text-slate-500 cursor-not-allowed")}
                                />
                            </div>
                        </div>
                        
                        {/* Payment Status - compact inline, extra compact on mobile */}
                        <div className="flex items-center gap-1.5 md:gap-3 mt-1 md:mt-2 pt-1 md:pt-2 border-t border-slate-200">
                            <Label className="text-[10px] md:text-[11px] font-medium text-slate-600">Payment Status</Label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={paymentStatus === 'Paid Now'}
                                    onChange={(e) => {
                                        const newStatus = e.target.checked ? 'Paid Now' : 'Due';
                                        const round2 = (n: number) => Math.round(n * 100) / 100;
                                        setPaymentStatus(newStatus);
                                        if (newStatus === 'Paid Now') {
                                            setCashPayment(round2(refundAmount));
                                            setCardPayment(0);
                                            setWalletPayment(0);
                                            setCouponPayment(0);
                                            setCreditNotePayment(0);
                                            setChequePayment(0);
                                            setDuePayment(0);
                                        } else {
                                            setCashPayment(0);
                                            setCardPayment(0);
                                            setWalletPayment(0);
                                            setCouponPayment(0);
                                            setCreditNotePayment(0);
                                            setChequePayment(0);
                                            setDuePayment(round2(refundAmount));
                                        }
                                    }}
                                    className="h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-[10px] md:text-[11px] font-medium text-slate-700">Paid Now</span>
                            </label>
                        </div>
                    </div>

                    {/* Action Buttons Row - compact, extra compact on mobile */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-1.5 md:gap-3 shrink-0">
                        {/* Cancel Button (Left) */}
                        <div className="w-full sm:w-auto">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleBackFromCreditNote}
                                className="w-full sm:w-auto border-slate-300 text-slate-600 hover:bg-slate-50 hover:text-slate-900 rounded-lg md:rounded-xl h-6 md:h-9 px-2.5 md:px-5 font-medium text-[11px] md:text-sm"
                            >
                                Cancel
                            </Button>
                        </div>

                        {/* Center: Valid Date - calendar icon opens date picker when clicked */}
                        <div className="flex items-center gap-1 md:gap-2 bg-slate-50 px-2 md:px-3 py-0.5 md:py-1 rounded-full border border-slate-200 shadow-sm w-full sm:w-auto justify-center">
                            <Label className="text-[10px] md:text-xs font-medium text-slate-500 whitespace-nowrap">Credit Note Valid Till</Label>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => {
                                        const input = creditNoteDateInputRef.current;
                                        if (input) {
                                            if (typeof (input as HTMLInputElement & { showPicker?: () => void }).showPicker === 'function') {
                                                (input as HTMLInputElement & { showPicker: () => void }).showPicker();
                                            } else {
                                                input.click();
                                            }
                                        }
                                    }}
                                    className="p-0.5 -m-0.5 rounded touch-manipulation"
                                    aria-label="Open date picker"
                                >
                                    <Calendar className="h-3.5 w-3.5 md:h-4 md:w-4 text-slate-500 shrink-0" />
                                </button>
                                <Input
                                    ref={creditNoteDateInputRef}
                                    type="date"
                                    value={creditNoteValidTill}
                                    onChange={(e) => setCreditNoteValidTill(e.target.value)}
                                    className="w-20 md:w-32 h-5 md:h-7 border-0 bg-transparent focus:ring-0 text-[10px] md:text-xs font-semibold text-slate-700 p-0"
                                />
                            </div>
                        </div>

                        {/* Generate Button (Right) */}
                        <div className="w-full sm:w-auto">
                            <Button
                                size="sm"
                                onClick={handleProcessRefund}
                                disabled={hasValidationErrors || !canGenerateCreditNote}
                                className={cn(
                                    "w-full sm:w-auto bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg shadow-purple-500/25 rounded-lg md:rounded-xl h-6 md:h-9 px-2.5 md:px-6 font-semibold text-[11px] md:text-sm transition-all active:scale-[0.98]",
                                    (hasValidationErrors || !canGenerateCreditNote) && "opacity-50 cursor-not-allowed shadow-none"
                                )}
                            >
                                <CheckCircle2 className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-1.5 shrink-0" />
                                Generate Credit Note
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Card */}
            {showSuccessCard && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <Card className="max-w-md w-full border-0 shadow-2xl animate-in zoom-in-95 duration-200 rounded-2xl overflow-hidden">
                        <CardHeader className="bg-green-50 border-b border-green-100 py-4">
                            <CardTitle className="text-lg font-bold text-green-900 flex items-center">
                                <CheckCircle2 className="h-5 w-5 mr-2 text-green-600" />
                                Refund Processed Successfully
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                            <div className="text-center space-y-4">
                                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                                </div>
                                <p className="text-slate-700 text-base font-medium">
                                    {successMessage}
                                </p>
                                <p className="text-slate-500 text-sm">
                                    Redirecting to refunds screen in 3 seconds...
                                </p>
                                <div className="w-full bg-green-100 rounded-full h-2 overflow-hidden">
                                    <div className="h-full bg-green-500 animate-pulse" style={{ animation: 'shrink 3s linear forwards' }}></div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Product Details Modal */}
            {selectedProduct && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
                    <Card className="max-w-2xl w-full border-0 shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto rounded-2xl">
                        <CardHeader className="bg-slate-50 border-b border-slate-200 py-4 sticky top-0 z-10">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg font-bold text-slate-900 flex items-center">
                                    <Eye className="h-5 w-5 mr-2 text-blue-600" />
                                    Product Details
                                </CardTitle>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setSelectedProduct(null)}
                                    className="h-8 w-8 rounded-full hover:bg-slate-200"
                                >
                                    <X className="h-4 w-4 text-slate-500" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-6 bg-white">
                            <div className="space-y-6">
                                {/* Basic Information */}
                                <div className="bg-slate-50 rounded-xl border border-slate-100 p-5">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Basic Information</h3>
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <div className="sm:col-span-2">
                                            <Label className="text-xs text-slate-500 uppercase">Product Name</Label>
                                            <p className="text-lg font-semibold text-slate-900 mt-1">{selectedProduct.name}</p>
                                        </div>
                                        {selectedProduct.productCode != null && (
                                            <div>
                                                <Label className="text-xs text-slate-500 uppercase">Product Code</Label>
                                                <p className="text-sm font-medium text-slate-800 mt-1">{selectedProduct.productCode}</p>
                                            </div>
                                        )}
                                        {selectedProduct.barcode && (
                                            <div>
                                                <Label className="text-xs text-slate-500 uppercase">Barcode</Label>
                                                <p className="text-sm font-mono font-medium text-slate-800 mt-1 break-all">{selectedProduct.barcode}</p>
                                            </div>
                                        )}
                                        {selectedProduct.hsnCode && (
                                            <div>
                                                <Label className="text-xs text-slate-500 uppercase">HSN Code</Label>
                                                <p className="text-sm font-medium text-slate-800 mt-1">{selectedProduct.hsnCode}</p>
                                            </div>
                                        )}
                                        {selectedProduct.serialNumber && (
                                            <div>
                                                <Label className="text-xs text-slate-500 uppercase">Serial Number</Label>
                                                <div className="mt-1">
                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-white border border-slate-200 text-slate-700 font-mono text-sm font-medium shadow-sm">
                                                        {selectedProduct.serialNumber}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <Label className="text-xs text-slate-500 uppercase">Quantity (Received)</Label>
                                            <p className="text-sm font-medium text-slate-800 mt-1">{selectedProduct.quantity}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Invoice Line Details (from invoice) */}
                                <div className="bg-slate-50 rounded-xl border border-slate-100 p-5">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Invoice Line Details</h3>
                                    <div className="grid gap-3 sm:grid-cols-2">
                                        <div className="flex justify-between sm:block">
                                            <Label className="text-xs text-slate-500 uppercase">Unit Cost</Label>
                                            <p className="text-sm font-semibold text-slate-900 mt-0.5">₹{selectedProduct.unitCost.toFixed(2)}</p>
                                        </div>
                                        {selectedProduct.mrp != null && selectedProduct.mrp > 0 && (
                                            <div className="flex justify-between sm:block">
                                                <Label className="text-xs text-slate-500 uppercase">MRP</Label>
                                                <p className="text-sm font-semibold text-slate-900 mt-0.5">₹{selectedProduct.mrp.toFixed(2)}</p>
                                            </div>
                                        )}
                                        <div className="flex justify-between sm:block">
                                            <Label className="text-xs text-slate-500 uppercase">Item Discount</Label>
                                            <p className="text-sm font-semibold text-slate-900 mt-0.5">₹{selectedProduct.discount.toFixed(2)}</p>
                                        </div>
                                        <div className="flex justify-between sm:block">
                                            <Label className="text-xs text-slate-500 uppercase">Taxable Value</Label>
                                            <p className="text-sm font-semibold text-slate-900 mt-0.5">₹{selectedProduct.taxableValue.toFixed(2)}</p>
                                        </div>
                                        <div className="flex justify-between sm:block">
                                            <Label className="text-xs text-slate-500 uppercase">SGST</Label>
                                            <p className="text-sm font-semibold text-slate-900 mt-0.5">₹{(selectedProduct.isIGST === 1 ? 0 : selectedProduct.sgst).toFixed(2)}</p>
                                        </div>
                                        <div className="flex justify-between sm:block">
                                            <Label className="text-xs text-slate-500 uppercase">CGST</Label>
                                            <p className="text-sm font-semibold text-slate-900 mt-0.5">₹{(selectedProduct.isIGST === 1 ? 0 : selectedProduct.cgst).toFixed(2)}</p>
                                        </div>
                                        <div className="flex justify-between sm:block">
                                            <Label className="text-xs text-slate-500 uppercase">IGST</Label>
                                            <p className="text-sm font-semibold text-slate-900 mt-0.5">₹{(selectedProduct.isIGST === 1 ? selectedProduct.igst : 0).toFixed(2)}</p>
                                        </div>
                                        <div className="flex justify-between sm:block sm:col-span-2">
                                            <Label className="text-xs text-slate-500 uppercase">Line Total</Label>
                                            <p className="text-sm font-bold text-slate-900 mt-0.5">₹{selectedProduct.totalAmount.toFixed(2)}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Refund breakdown & Return quantity */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-blue-50/50 rounded-xl border border-blue-100 p-4">
                                        <h3 className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-3">Refund Breakdown</h3>
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-slate-500">Taxable</span>
                                                <span className="text-sm font-semibold text-slate-900">₹{selectedProduct.refundTaxable.toFixed(2)}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-sm text-slate-500">Total Tax</span>
                                                <span className="text-sm font-semibold text-slate-900">₹{(selectedProduct.refundSGST + selectedProduct.refundCGST + selectedProduct.refundIGST + selectedProduct.refundCess).toFixed(2)}</span>
                                            </div>
                                            <div className="pt-2 mt-2 border-t border-blue-100 flex justify-between">
                                                <span className="text-sm font-bold text-blue-700">Total Refund</span>
                                                <span className="text-sm font-bold text-blue-700">₹{selectedProduct.refundAmount.toFixed(2)}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 flex flex-col justify-center items-center text-center">
                                        <span className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Return Quantity</span>
                                        <span className="text-3xl font-extrabold text-slate-900">{selectedProduct.refundQty}</span>
                                        <span className="text-xs text-slate-500 mt-1">of {selectedProduct.quantity} received</span>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-8 flex justify-end">
                                <Button
                                    onClick={() => setSelectedProduct(null)}
                                    className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-6"
                                >
                                    Close Details
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default ReturnProcessPage;
